import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormLabelSpan from 'components/layout/PraxFormLabelSpan';
import PxGrid from 'components/layout/PxGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header, Icon, RadioProps, Segment } from 'semantic-ui-react';
import axios from 'services/http';
import { updateBillXmlResponsesFromMediData } from 'services/mediDataServices';
import styled from 'styled-components';
import { TariffDto } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';

const StyledForm = styled.form`
  & .ui.grid > .row {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }

  & .ui.grid > .row:first-child {
    padding-bottom: 1rem !important;
  }

  & .ui.grid {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
`;

const SearchSegment = styled(Segment)`

  &.ui.segment, .ui.segments &.segment {
    padding-left: 0;
    padding-bottom: 0;
  }
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  onSearchResponses: (mandateId: number, tariffId: number, billId?: number, patientId?: number) => void,
  onResponseLoadingChange: (documentsLoading: boolean) => void,
  onResponsesUpdating: (responsesUpdating: boolean) => void,
  onSetErrorMessages: (errorMessages: Array<string>) => void,
  errorMessages: Array<string>,
  responsesUpdating: boolean,
  responseLoading: boolean,
  tariffs: Array<TariffDto>
}

interface XmlResponseSearchData {
  mandateId: number;
  tariffId?: number;
  billId?: number;
  patientId?: number;
}

class MediDataDownloadSearchView extends Component<Props> {

  initialValues: Partial<XmlResponseSearchData> = {};
  radioMap: Map<string, RadioProps> = new Map<string, RadioProps>();
  cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  constructor(props: Props) {
    super(props);

    this.initialValues = this.getInitialValues(props);
  }

  getInitialValues = (props: Props): Partial<XmlResponseSearchData> => {

    const {tariffs, currentUser} = props;

    return {
      mandateId: currentUser ? currentUser.userConfig.mandateId : undefined,
      tariffId: (tariffs && tariffs.length > 0) ? tariffs[0].id : undefined
    }
  };

  updateBillXmlResponse = async () => {

    const {onResponsesUpdating, currentUser} = this.props;

    onResponsesUpdating(true);

    try {
      let mandateId = currentUser!.userConfig.mandateId;
      await updateBillXmlResponsesFromMediData(mandateId, this.cancelTokenSource);
    } catch (e) {
      this.handleError(e.response.data);
    } finally {
      onResponsesUpdating(false);
    }
  }

  handleChange = (values: Partial<XmlResponseSearchData>) => {

    const {onSearchResponses} = this.props;

    const tariffId = values.tariffId as number;
    const mandateId = values.mandateId as number;
    const billId = values.billId;
    const patientId = values.patientId;

    onSearchResponses(mandateId, tariffId, billId, patientId);
  };

  handleError = (error: any) => {
    const {errorMessages, getMessage} = this.props;

    if (error) {
      const status = error.status;
      const exception: string = error.exception;

      if (status === 500 && exception.includes("MediDataProcessException")) {
        this.setErrorMessage(getMessage("error.mediDataProcessException"))
        return;
      }

      const errorCode = error.errorCode;
      const knownErrors: Array<string> = [
        errorUtils.unableToAcquireLock
      ];

      const violations: Array<any> = error.violations;

      if (violations && violations.length > 0) {
        violations.forEach(violation => {
          if (knownErrors.includes(violation.errorCode)) {
            this.setErrorMessage(getMessage(`error.${violation.errorCode}`));
          }
        });
      } else {
        if (knownErrors.includes(errorCode)) {
          this.setErrorMessage(getMessage(`error.${errorCode}`));
        } else {
          this.setErrorMessage(getMessage('error.general'));
        }
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {

    const {errorMessages, onSetErrorMessages} = this.props;

    if (errorMessage) {

      const errMsgs = [...errorMessages];
      errMsgs.push(errorMessage);

      onSetErrorMessages(errMsgs);
    } else {
      onSetErrorMessages([]);
    }
  };

  render(): React.ReactNode {
    return (
        <SearchSegment>
          <FinalForm
              onSubmit={(values, form) => {
                this.handleChange(values);
              }}
              initialValues={this.initialValues}
              render={this.renderForm}
          />
        </SearchSegment>
    );
  }

  renderForm = ({handleSubmit, values}: FormRenderProps): React.ReactNode => {

    const {props} = this;
    const {responsesUpdating, tariffs, errorMessages, getMessage} = props;

    const tariffOptions: Array<DropdownOption> = tariffs.map(tariff => ({
      key: tariff.id,
      text: tariff.description,
      value: tariff.id
    }));

    return (
        <StyledForm
            onSubmit={handleSubmit}
        >
          <FormContainer>
            <FormContent>
              <PxGrid>
                {errorMessages.length > 0 &&
                    <Grid.Row>
                      <Grid.Column>
                        <StyledErrorMessage
                            onDismiss={() => this.setErrorMessage(undefined)}>
                          {errorMessages.map(err => <div key={err}>{err}</div>)}
                        </StyledErrorMessage>
                      </Grid.Column>
                    </Grid.Row>
                }
                <Grid.Row>
                  <Grid.Column width={13} verticalAlign="middle">
                    <Header>{getMessage("searchOptions")}</Header>
                  </Grid.Column>
                  <Grid.Column width={3} verticalAlign="middle" textAlign="right">
                    <Button icon
                            primary
                            size="small"
                            compact
                            disabled={responsesUpdating}
                            onClick={this.updateBillXmlResponse}>
                      <Icon name="refresh"/>&nbsp;&nbsp;{getMessage("mediData.updateResponses")}
                    </Button>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>

                  <Grid.Column width={3} verticalAlign="middle">
                    <PraxFormLabelSpan htmlFor="tariffId">{getMessage('tariff')}</PraxFormLabelSpan>
                    <Field
                        name="tariffId"
                        component={Select}
                        placeholder={`${getMessage('select')} ${getMessage('tariff')}`}
                        options={tariffOptions}
                        validate={value => {
                          return isNaN(value) ? "required" : undefined;
                        }}
                        fluid
                    />
                  </Grid.Column>

                  <Grid.Column width={2} verticalAlign="middle">
                    <PraxFormLabelSpan htmlFor="billId">{getMessage('common.billId')}</PraxFormLabelSpan>
                    <Field
                        name="billId"
                        component={Input}
                        fluid
                    />
                  </Grid.Column>

                  <Grid.Column width={2} verticalAlign="middle">
                    <PraxFormLabelSpan htmlFor="billId">{getMessage('common.patientId')}</PraxFormLabelSpan>
                    <Field
                        name="patientId"
                        component={Input}
                        fluid
                    />
                  </Grid.Column>

                  <Grid.Column width={1} verticalAlign="bottom">
                    <Button type="button" primary size="tiny" compact onClick={evt => handleSubmit()}>
                      {getMessage("search")}
                    </Button>
                  </Grid.Column>
                </Grid.Row>
              </PxGrid>
            </FormContent>
          </FormContainer>
        </StyledForm>
    )
  }
}

export default withMessages(withAuthContext(MediDataDownloadSearchView));
